import React from "react";
import { Box } from "@mui/material";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import { BUTTON_RESET, C, FONT_BODY, FOCUS_RING, RADIUS, TYPE } from "../../utils/designTokens";
import { fieldSx, labelSx, metaSx, money } from "./checkoutUi";

/**
 * RMF Wallet, first in Payment: type how much credit to use, or Use All.
 * Checkout keeps the amount within the balance and the order total.
 */
function WalletCard({ available, storeCredit = 0, inputValue, onInputChange, onUseAll, applied, chargedToCard, currencySymbol = "$" }) {
  const on = applied > 0;
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: RADIUS.menuCard,
        border: `1px solid ${on ? C.goldAccent : C.borderStrong}`,
        bgcolor: on ? C.goldSoft : C.bg,
        transition: "background-color .2s ease, border-color .2s ease",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box
          aria-hidden="true"
          sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: "50%", bgcolor: on ? C.bg : C.goldSoft, color: C.gold, display: "flex", alignItems: "center", justifyContent: "center" }}
        >
          <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ ...TYPE.subHeading, flex: 1, minWidth: 0, color: C.ink }}>RMF Wallet</Box>
        <Box sx={{ ...metaSx, color: C.gold, fontWeight: 600 }}>{`${money(available, currencySymbol)} available`}</Box>
      </Box>
      {storeCredit > 0 && (
        <Box sx={{ ...metaSx, mt: 1.25 }}>
          {`Includes ${money(storeCredit, currencySymbol)} store credit at this restaurant, used first.`}
        </Box>
      )}

      <Box component="label" htmlFor="checkout-wallet-amount" sx={{ ...labelSx, display: "block", mt: 1.75, mb: 0.75 }}>
        Amount to use
      </Box>
      <Box sx={{ display: "flex", gap: 1 }}>
        <Box
          component="input"
          id="checkout-wallet-amount"
          type="number"
          inputMode="decimal"
          min={0}
          max={available}
          step={0.01}
          placeholder="Amount to use"
          value={inputValue}
          onChange={(e) => onInputChange(e.target.value)}
          sx={{ ...fieldSx, flex: 1, minWidth: 0 }}
        />
        <Box
          component="button"
          type="button"
          onClick={onUseAll}
          sx={{
            ...BUTTON_RESET,
            flexShrink: 0,
            height: 44,
            px: 2,
            borderRadius: RADIUS.control,
            border: `1px solid ${C.ink}`,
            bgcolor: C.bg,
            fontFamily: FONT_BODY,
            fontSize: 14,
            fontWeight: 700,
            color: C.ink,
            whiteSpace: "nowrap",
            "&:hover": { bgcolor: C.surface },
            ...FOCUS_RING,
          }}
        >
          Use All
        </Box>
      </Box>

      {on && (
        <Box component="dl" aria-live="polite" sx={{ m: 0, mt: 1.5 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
            <Box component="dt" sx={metaSx}>Wallet credit applied</Box>
            <Box component="dd" sx={{ ...metaSx, m: 0, fontWeight: 700, color: C.green, fontVariantNumeric: "tabular-nums" }}>
              {`−${money(applied, currencySymbol)}`}
            </Box>
          </Box>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 2, mt: 0.5 }}>
            <Box component="dt" sx={metaSx}>Charged to card</Box>
            <Box component="dd" sx={{ ...metaSx, m: 0, fontWeight: 700, color: C.ink, fontVariantNumeric: "tabular-nums" }}>
              {money(chargedToCard, currencySymbol)}
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default React.memo(WalletCard);
